import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  captureAttributionFromLocation,
  saveMetrikaClientId,
} from "../../aouth/referral/attributionStorage";

export const YANDEX_METRIKA_COUNTER_ID = 96176063;
// Счётчик работает только на боевом сайте: запуски на компьютере разработчика
// и проверки сборки не должны попадать в статистику. Браузер отдаёт
// кириллический домен в punycode, поэтому сравниваем с ним.
export const YANDEX_METRIKA_HOSTS = Object.freeze([
  "xn--h1alceggc.xn--p1ai",
  "www.xn--h1alceggc.xn--p1ai",
]);

export function isYandexMetrikaHost(hostname) {
  return YANDEX_METRIKA_HOSTS.includes(String(hostname || "").toLowerCase());
}

export const isYandexMetrikaEnabled =
  typeof window !== "undefined" && isYandexMetrikaHost(window.location?.hostname);

export const METRIKA_GOALS = Object.freeze({
  appPageView: "app_page_view",
  apiSaveStarted: "api_save_started",
  apiSaveSuccess: "api_save_success",
  apiSaveError: "api_save_error",
  apiPageOpened: "api_page_opened",
  aiPageOpened: "ai_page_opened",
  aiRecommendationOpen: "ai_recommendation_open",
  aiRecommendationStatusChange: "ai_recommendation_status_change",
  aiRecommendationTaskCreated: "ai_recommendation_task_created",
  aiRecommendationsExportStarted: "ai_recommendations_export_started",
  aiRecommendationsExportSuccess: "ai_recommendations_export_success",
  aiRecommendationsExportError: "ai_recommendations_export_error",
  applicationPageOpened: "application_page_opened",
  chatPageOpened: "chat_page_opened",
  companyChanged: "company_changed",
  companyPageOpened: "company_page_opened",
  costsPageOpened: "costs_page_opened",
  dashboardPageOpened: "dashboard_page_opened",
  landingTrialClick: "landing_trial_click",
  landingCallbackClick: "landing_callback_click",
  landingFaqOpen: "landing_faq_open",
  landingScenarioSelect: "landing_scenario_select",
  calcPublicDone: "calc_public_done",
  calcPublicPriceSolve: "calc_public_price_solve",
  calcPublicConnectClick: "calc_public_connect_click",
  authCodeRequested: "auth_code_requested",
  // Вход завершён: звонок засчитан, человек попадает в кабинет. new_user — номер новый.
  authLoginSuccess: "auth_login_success",
  // Новое меню сломалось, вместо него показали старое.
  navV3Fallback: "nav_v3_fallback",
  // Новая страница раздела сломалась, вместо неё показали прежнюю (PageV3Switch).
  pageV3Fallback: "page_v3_fallback",
  navigationClick: "navigation_click",
  ordersPageOpened: "orders_page_opened",
  paymentAutopayAccepted: "payment_autopay_accepted",
  paymentAutopayCancelled: "payment_autopay_cancelled",
  paymentAutopayCancelError: "payment_autopay_cancel_error",
  paymentPageOpened: "payment_page_opened",
  paymentModalOpened: "payment_modal_opened",
  paymentPromoApplyStarted: "payment_promo_apply_started",
  paymentPromoApplySuccess: "payment_promo_apply_success",
  paymentPromoApplyError: "payment_promo_apply_error",
  paymentStarted: "payment_started",
  paymentRedirectCreated: "payment_redirect_created",
  paymentError: "payment_error",
  paymentSuccess: "payment_success",
  registrationNewUser: "registration_new_user",
  profilePageOpened: "profile_page_opened",
  referralPageOpened: "referral_page_opened",
  settingsPageOpened: "settings_page_opened",
  taskCreated: "task_created",
  taskDetailsPageOpened: "task_details_page_opened",
  tasksPageOpened: "tasks_page_opened",
  unitPageOpened: "unit_page_opened",
  videoPageOpened: "video_page_opened",
});

let queuedCommands = [];
let lastPageHit = { url: "", at: 0 };
let lastPageGoal = { key: "", at: 0 };
let lastContextKey = "";

const PAGE_ANALYTICS_RULES = [
  {
    pattern: /^\/$/,
    pageKey: "home",
    section: "home",
    goalName: METRIKA_GOALS.dashboardPageOpened,
  },
  {
    pattern: /^\/wildberries\/wbDashboard(?:\/)?$/,
    pageKey: "wb_orders",
    section: "orders",
    marketplace: "wb",
    goalName: METRIKA_GOALS.ordersPageOpened,
  },
  {
    pattern: /^\/ozon\/ozonDashboard(?:\/)?$/,
    pageKey: "ozon_orders",
    section: "orders",
    marketplace: "ozon",
    goalName: METRIKA_GOALS.ordersPageOpened,
  },
  {
    pattern: /^\/wildberries\/wbUnit(?:\/)?$/,
    pageKey: "wb_unit",
    section: "unit",
    marketplace: "wb",
    goalName: METRIKA_GOALS.unitPageOpened,
  },
  {
    pattern: /^\/ozon\/ozonUnit(?:\/)?$/,
    pageKey: "ozon_unit",
    section: "unit",
    marketplace: "ozon",
    goalName: METRIKA_GOALS.unitPageOpened,
  },
  {
    pattern: /^\/wildberries\/wbAI(?:\/)?$/,
    pageKey: "wb_ai_recommendations",
    section: "ai_recommendations",
    marketplace: "wb",
    goalName: METRIKA_GOALS.aiPageOpened,
  },
  {
    pattern: /^\/ozon\/ozonAI(?:\/)?$/,
    pageKey: "ozon_ai_recommendations",
    section: "ai_recommendations",
    marketplace: "ozon",
    goalName: METRIKA_GOALS.aiPageOpened,
  },
  {
    pattern: /^\/wildberries\/costs(?:\/)?$/,
    pageKey: "wb_costs",
    section: "costs",
    marketplace: "wb",
    goalName: METRIKA_GOALS.costsPageOpened,
  },
  {
    pattern: /^\/ozon\/costs(?:\/)?$/,
    pageKey: "ozon_costs",
    section: "costs",
    marketplace: "ozon",
    goalName: METRIKA_GOALS.costsPageOpened,
  },
  {
    pattern: /^\/task(?:\/)?$/,
    pageKey: "tasks",
    section: "tasks",
    goalName: METRIKA_GOALS.tasksPageOpened,
  },
  {
    pattern: /^\/creattask(?:\/)?$/,
    pageKey: "task_create",
    section: "tasks",
    goalName: METRIKA_GOALS.tasksPageOpened,
  },
  {
    pattern: /^\/getCurrentTask\//,
    pageKey: "task_details",
    section: "tasks",
    goalName: METRIKA_GOALS.taskDetailsPageOpened,
  },
  {
    pattern: /^\/editChild\//,
    pageKey: "task_child_edit",
    section: "tasks",
    goalName: METRIKA_GOALS.taskDetailsPageOpened,
  },
  {
    pattern: /^\/changeApi(?:\/)?$/,
    pageKey: "api_keys",
    section: "api",
    goalName: METRIKA_GOALS.apiPageOpened,
  },
  {
    pattern: /^\/payment(?:\/)?$/,
    pageKey: "payment",
    section: "billing",
    goalName: METRIKA_GOALS.paymentPageOpened,
  },
  {
    pattern: /^\/mydata(?:\/)?$/,
    pageKey: "profile",
    section: "profile",
    goalName: METRIKA_GOALS.profilePageOpened,
  },
  {
    pattern: /^\/settings(?:\/|$)/,
    pageKey: "settings",
    section: "settings",
    goalName: METRIKA_GOALS.settingsPageOpened,
  },
  {
    pattern: /^\/chat(?:\/|$)/,
    pageKey: "chat",
    section: "chat",
    goalName: METRIKA_GOALS.chatPageOpened,
  },
  {
    pattern: /^\/application(?:\/)?$/,
    pageKey: "application",
    section: "application",
    goalName: METRIKA_GOALS.applicationPageOpened,
  },
  {
    pattern: /^\/referral(?:\/)?$/,
    pageKey: "referral",
    section: "referral",
    goalName: METRIKA_GOALS.referralPageOpened,
  },
  {
    pattern: /^\/videCall(?:\/)?$/,
    pageKey: "video_call",
    section: "video",
    goalName: METRIKA_GOALS.videoPageOpened,
  },
  {
    pattern: /^\/addnewcompany(?:\/)?$/,
    pageKey: "company_add",
    section: "company",
    goalName: METRIKA_GOALS.companyPageOpened,
  },
  {
    pattern: /^\/EditCompanyOption(?:\/)?$/,
    pageKey: "company_settings",
    section: "company",
    goalName: METRIKA_GOALS.companyPageOpened,
  },
  {
    pattern: /^\/statistic(?:\/)?$/,
    pageKey: "statistic",
    section: "analytics",
    goalName: METRIKA_GOALS.dashboardPageOpened,
  },
];

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function sanitizeMetrikaValue(value, depth = 0) {
  if (depth > 3) return undefined;
  if (value === null || value === undefined) return undefined;

  if (typeof value === "string") {
    const text = value.trim();
    return text ? text.slice(0, 200) : undefined;
  }

  if (typeof value === "bigint") {
    return String(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    const list = value
      .map((item) => sanitizeMetrikaValue(item, depth + 1))
      .filter((item) => item !== undefined);
    return list.length ? list : undefined;
  }

  if (isPlainObject(value)) {
    const result = Object.entries(value).reduce((acc, [key, item]) => {
      const safeKey = String(key).trim();
      const safeValue = sanitizeMetrikaValue(item, depth + 1);
      if (safeKey && safeValue !== undefined) acc[safeKey] = safeValue;
      return acc;
    }, {});

    return Object.keys(result).length ? result : undefined;
  }

  return undefined;
}

function normalizePagePath(pageUrl = "") {
  const path = String(pageUrl || "/").split(/[?#]/)[0] || "/";
  if (path === "/") return path;
  return path.replace(/\/+$/, "");
}

export function getMetrikaPageAnalytics(pageUrl = "") {
  const pagePath = normalizePagePath(pageUrl);
  const rule = PAGE_ANALYTICS_RULES.find(({ pattern }) =>
    pattern.test(pagePath)
  );

  return {
    app_area: "internal_app",
    page_path: pagePath,
    page_key: rule?.pageKey || "other",
    page_section: rule?.section || "other",
    marketplace: rule?.marketplace,
    goalName: rule?.goalName,
  };
}

function getMetrikaPageParams(pageAnalytics = {}) {
  const { goalName, ...params } = pageAnalytics;
  return params;
}

function trackMetrikaPageOpen(pageUrl, pageAnalytics) {
  // /login — это сайт и вход, а не кабинет: иначе каждый визит с рекламы
  // считался бы «просмотром страницы приложения».
  if (/^\/login(?:\/|$)/.test(pageAnalytics?.page_path || "")) return false;

  const normalizedUrl = String(pageUrl || "/");
  const pageKey = `${normalizedUrl}:${pageAnalytics?.page_key || "other"}`;
  const now = Date.now();

  if (lastPageGoal.key === pageKey && now - lastPageGoal.at < 1000) {
    return false;
  }

  lastPageGoal = { key: pageKey, at: now };

  const pageParams = getMetrikaPageParams(pageAnalytics);
  trackMetrikaGoal(METRIKA_GOALS.appPageView, pageParams);

  if (pageAnalytics?.goalName) {
    trackMetrikaGoal(pageAnalytics.goalName, pageParams);
  }

  return true;
}

function getYandexMetrikaCounter() {
  if (typeof window === "undefined") return null;
  return window[`yaCounter${YANDEX_METRIKA_COUNTER_ID}`] || null;
}

function callYandexMetrika(method, args = []) {
  if (typeof window === "undefined") return false;

  if (typeof window.ym === "function") {
    window.ym(YANDEX_METRIKA_COUNTER_ID, method, ...args);
    return true;
  }

  const counter = getYandexMetrikaCounter();
  if (counter && typeof counter[method] === "function") {
    counter[method](...args);
    return true;
  }

  return false;
}

function isYandexMetrikaReady() {
  if (typeof window === "undefined") return false;
  return typeof window.ym === "function" || Boolean(getYandexMetrikaCounter());
}

function flushMetrikaQueue() {
  if (!queuedCommands.length) return;

  if (!isYandexMetrikaReady()) return;

  const commands = queuedCommands;
  queuedCommands = [];

  commands.forEach(([method, args]) => {
    try {
      callYandexMetrika(method, args);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Yandex Metrika command failed", method, error);
      }
    }
  });
}

export function sendYandexMetrika(method, ...args) {
  if (!isYandexMetrikaEnabled || !method) return false;

  try {
    const sent = callYandexMetrika(method, args);
    if (!sent) {
      queuedCommands.push([method, args]);
      return false;
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Yandex Metrika command failed", method, error);
    }
    return false;
  }
}

export function trackMetrikaHit(url, options = {}) {
  if (!url) return false;

  // Счётчик запускается в public/index.html и сам отправляет первый просмотр.
  if (typeof window !== "undefined" && window.__uprastiYmFirstHit) {
    window.__uprastiYmFirstHit = false;
    return false;
  }

  const normalizedUrl = String(url);
  const now = Date.now();

  if (lastPageHit.url === normalizedUrl && now - lastPageHit.at < 1000) {
    return false;
  }

  lastPageHit = { url: normalizedUrl, at: now };

  return sendYandexMetrika("hit", normalizedUrl, {
    title: typeof document !== "undefined" ? document.title : undefined,
    ...sanitizeMetrikaValue(options),
  });
}

export function trackMetrikaGoal(goalName, params = {}) {
  if (!goalName) return false;
  return sendYandexMetrika(
    "reachGoal",
    goalName,
    sanitizeMetrikaValue(params) || {}
  );
}

export function setMetrikaParams(params = {}) {
  const safeParams = sanitizeMetrikaValue(params);
  if (!safeParams) return false;
  return sendYandexMetrika("params", safeParams);
}

export function setMetrikaUserParams(params = {}) {
  const safeParams = sanitizeMetrikaValue(params);
  if (!safeParams) return false;
  return sendYandexMetrika("userParams", safeParams);
}

function readGoalDataset(element) {
  const goalName = element?.dataset?.ymGoal;
  if (!goalName) return null;

  const rawPayload = element.dataset.ymPayload;
  if (!rawPayload) return { goalName, params: {} };

  try {
    return { goalName, params: JSON.parse(rawPayload) };
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Invalid data-ym-payload", rawPayload, error);
    }
    return { goalName, params: {} };
  }
}

// Robokassa возвращает пользователя на /payment?paid=1&inv=...&sum=... (routes/payment/robokassa.js).
function trackPaymentSuccessFromLocation(search = "") {
  const params = new URLSearchParams(search);
  if (params.get("paid") !== "1") return;

  const invoiceId = params.get("inv") || "";
  const storageKey = `uprasti_payment_goal_${invoiceId}`;

  try {
    if (invoiceId && window.sessionStorage.getItem(storageKey)) return;
    if (invoiceId) window.sessionStorage.setItem(storageKey, "1");
  } catch (_) {}

  const sum = Number(params.get("sum"));
  trackMetrikaGoal(METRIKA_GOALS.paymentSuccess, {
    order_price: Number.isFinite(sum) && sum > 0 ? sum : undefined,
    currency: "RUB",
    invoice_id: invoiceId || undefined,
  });
}

function getUserRole(user) {
  if (!user || typeof user !== "object") return "guest";
  return user.role || user.type || user.status || "user";
}

export default function YandexMetric() {
  const location = useLocation();
  const currentUser = useSelector((state) => state.getMayInfoReducer?.myinfo);
  const selectedCompanyId = useSelector(
    (state) => state.checkedSessionReducer?.checkedSession
  );
  const currentCompany = useSelector(
    (state) => state.getMyCompanyAPIReducer?.company
  );

  const pageUrl = useMemo(() => {
    return `${location.pathname}${location.search}${location.hash || ""}`;
  }, [location.hash, location.pathname, location.search]);

  const pageAnalytics = useMemo(
    () => getMetrikaPageAnalytics(pageUrl),
    [pageUrl]
  );

  const visitContext = useMemo(() => {
    return {
      app: "uprasti",
      signed_in: Boolean(currentUser?._id),
      user_role: getUserRole(currentUser),
      company_selected: Boolean(selectedCompanyId || currentCompany?._id),
      subscription_status: currentCompany?.subscriptionStatus || "unknown",
      has_wb_api: Boolean(currentCompany?.walberisToken),
      has_ozon_api: Boolean(
        currentCompany?.ozonToken && currentCompany?.ozonClientID
      ),
      has_ozon_ads_api: Boolean(
        currentCompany?.ozonTokenReclam && currentCompany?.ozonClientIDReclam
      ),
    };
  }, [
    currentCompany?._id,
    currentCompany?.ozonClientID,
    currentCompany?.ozonClientIDReclam,
    currentCompany?.ozonToken,
    currentCompany?.ozonTokenReclam,
    currentCompany?.subscriptionStatus,
    currentCompany?.walberisToken,
    currentUser,
    selectedCompanyId,
  ]);

  useEffect(() => {
    captureAttributionFromLocation(location.search);
    trackPaymentSuccessFromLocation(location.search);
  }, [location.search]);

  useEffect(() => {
    sendYandexMetrika("getClientID", saveMetrikaClientId);
  }, []);

  useEffect(() => {
    flushMetrikaQueue();
    const intervalId = window.setInterval(flushMetrikaQueue, 500);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const pageParams = getMetrikaPageParams(pageAnalytics);
    trackMetrikaHit(pageUrl, pageParams);
    trackMetrikaPageOpen(pageUrl, pageAnalytics);
  }, [pageAnalytics, pageUrl]);

  useEffect(() => {
    const contextKey = JSON.stringify(visitContext);
    if (contextKey === lastContextKey) return;

    lastContextKey = contextKey;
    setMetrikaParams(visitContext);
    setMetrikaUserParams(visitContext);
  }, [visitContext]);

  useEffect(() => {
    const handleGoalClick = (event) => {
      const target = event.target?.closest?.("[data-ym-goal]");
      const goal = readGoalDataset(target);
      if (!goal) return;
      trackMetrikaGoal(goal.goalName, goal.params);
    };

    document.addEventListener("click", handleGoalClick, true);
    return () => {
      document.removeEventListener("click", handleGoalClick, true);
    };
  }, []);

  // Сам счётчик (tag.js и init) подключается в public/index.html, до загрузки приложения.
  return null;
}
